import { IDEAL_ANGLES, getIdealAngles } from "@/ai/idealAngles";
import { LIFTS } from "@/ai/liftPose";
import { gymHolds, sportHolds } from "@/ai/hold/holds";
import { SKILLS, SKILL_ALIASES } from "@/ai/skills/index";

/**
 * What live practice can coach. Racquet sports only for now: the moment that
 * matters is a swing, and the swing detector keys on hitting-arm speed. Every
 * shot listed has curated target angles (idealAngles); none are invented here.
 */
export const PRACTICE_SPORTS = [
  { key: "badminton", label: "Badminton" },
  { key: "tennis", label: "Tennis" },
  { key: "table_tennis", label: "Table tennis" },
  { key: "pickleball", label: "Pickleball" },
  { key: "squash", label: "Squash" },
];

/** Lifting is practised too, but it is its own thing: reps judged at the setup and the lockout, not a swing at contact. */
export const LIFT_SPORT = { key: "strength", label: "Lifting" };
export const isLiftSport = (s) => s === LIFT_SPORT.key;

/**
 * What people see: two groups, Gym (bodyweight and gym-floor work) and Lifting (weights). Both run on the lift engine and record as sport "strength" (links, history and
 * the guide pages keep that key); the group only decides which exercises are listed. Squat is in both: it is done with and without weight.
 * An exercise in neither list belongs to Lifting, so a new lift always shows up somewhere.
 */
export const LIFT_GROUPS = [{ key: "gym", label: "Gym" }, { key: "lifting", label: "Lifting" }];
const IN_GYM = new Set(["squat", "pushup", "lunge", "glute_bridge", "pullup", "dip", "situp", "jumping_jack", "plank", "wall_sit"]);
const IN_LIFTING = new Set(["deadlift", "squat", "ohp", "row", "rdl", "curl", "tricep", "lateral_raise"]);
export const isLiftGroup = (k) => LIFT_GROUPS.some((g) => g.key === k);
export const liftGroupsOf = (key) => [IN_GYM.has(key) && "gym", (IN_LIFTING.has(key) || !IN_GYM.has(key)) && "lifting"].filter(Boolean);
/** Which group a link opens: the asked one when the exercise is in it, else the exercise's own, else Lifting. */
export const liftGroupFor = (shotKey, asked) => {
  const g = liftGroupsOf(shotKey || "");
  if (shotKey && asked && g.includes(asked)) return asked;
  if (shotKey && g.length) return g[0];
  return isLiftGroup(asked) ? asked : "lifting";
};
export const liftShots = (group) => [...Object.values(LIFTS), ...gymHolds()]
  .filter((l) => !group || liftGroupsOf(l.key).includes(group))
  .map((l) => ({ key: l.key, label: l.label, name: l.label, beta: l.status === "beta", hold: l.kind === "hold" }));

const prettyKey = (k) => k.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

/** "Smash — overhead contact" → "Smash". */
export const shortShotName = (label, key) => (label ? label.split(/\s+[—-]\s+/)[0] : prettyKey(key));

export function shotsFor(sport) {
  const table = IDEAL_ANGLES[sport];
  if (!table) return [];
  return Object.entries(table).map(([key, v]) => ({ key, label: v.label || prettyKey(key), name: shortShotName(v.label, key) }));
}

/**
 * Turn free text from the analysis ("Forehand Drive - Flat Crosscourt") into a
 * practiceable shot for `sport`, or null when we have no targets for it.
 */
export function resolveShot(sport, shotText) {
  const table = IDEAL_ANGLES[sport];
  if (!table) return null;
  if (table[shotText]) return { key: shotText, label: table[shotText].label, ideal: table[shotText] };
  const ideal = getIdealAngles(sport, shotText);
  if (!ideal) return null;
  const key = Object.keys(table).find((k) => table[k] === ideal);
  return key ? { key, label: ideal.label, ideal } : null;
}

export const isPracticeSport = (s) => PRACTICE_SPORTS.some((p) => p.key === s);

/**
 * Skills practised one event at a time (a shot), not as swings at contact nor as reps: basketball and cricket batting. Kept apart from
 * PRACTICE_SPORTS on purpose: the analysis contact ranges in idealAngles also cover cricket and basketball, and the racquet screen must
 * never be offered them (its "shots" list would show wicket-keeping and fast bowling).
 */
export const SKILL_SPORTS = [{ key: "basketball", label: "Basketball", beta: true }, { key: "cricket", label: "Cricket", beta: true }];
export const isSkillSport = (s) => SKILL_SPORTS.some((p) => p.key === s);
const norm = (s) => String(s || "").toLowerCase().trim().replace(/[\s-]+/g, "_");
export const skillShots = (sport) => [
  ...Object.values(SKILLS[sport] || {}).filter((s) => s.status !== "hidden")
    .map((s) => ({ key: s.key, label: s.label, name: s.name || s.label, group: s.group || null, beta: s.status === "beta" })),
  ...sportHolds(sport).map((h) => ({ key: h.key, label: h.label, name: h.label, group: "Stance", beta: h.status === "beta", hold: true })),
];
/** A skill key for free text ("Free throw", "jump shot"), or null. */
export function resolveSkill(sport, text) {
  const t = norm(text);
  const hit = SKILLS[sport]?.[t] || SKILLS[sport]?.[SKILL_ALIASES[sport]?.[t]];
  return hit && hit.status !== "hidden" ? hit : null;
}
/** Which engine runs a sport: "lift" | "skill" | "racquet" | null. */
export const practiceKind = (s) => (isLiftSport(s) ? "lift" : isSkillSport(s) ? "skill" : isPracticeSport(s) ? "racquet" : null);

/** The link that opens live practice for a sport + shot (+ the joint to work on). */
export function practiceUrl({ sport, shot, focus }) {
  const q = new URLSearchParams();
  if (sport) q.set("sport", sport);
  if (shot) q.set("shot", shot);
  if (focus) q.set("focus", focus);
  return `/practice?${q.toString()}`;
}
